import type { Command } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { getParameters } from '@atlaskit/native-embeds-common';

import { pluginKey } from '../plugin-state';

export const getNativeEmbedUrl = (selectedNode: PMNode): string | undefined => {
	return getParameters(selectedNode.attrs.parameters, 'url');
};

export const createOpenInNewWindowCommand =
	(selectedNode: PMNode): Command =>
	() => {
		const url = getNativeEmbedUrl(selectedNode);
		if (url) {
			window.open(url, '_blank', 'noopener,noreferrer');
		}
		return true;
	};

/**
 * @private
 * @deprecated Use `createCopyNodeCommand` instead. This copies only the URL string to clipboard.
 */
export const createCopyLinkCommand =
	(selectedNode: PMNode): Command =>
	() => {
		const url = getNativeEmbedUrl(selectedNode);
		if (url) {
			navigator.clipboard.writeText(url);
		}
		return true;
	};

export const showUrlToolbar: Command = (state, dispatch) => {
	dispatch?.(state.tr.setMeta(pluginKey, { type: 'SHOW_URL_TOOLBAR' }));
	return true;
};

export const hideUrlToolbar: Command = (state, dispatch) => {
	dispatch?.(state.tr.setMeta(pluginKey, { type: 'HIDE_URL_TOOLBAR' }));
	return true;
};

/**
 * Creates a command that replaces the currently selected native embed extension
 * node with an embedCard node using the provided URL.
 *
 * Used when the user edits the URL of a native embed to a URL that is no longer
 * supported by any registered native embed experience — in that case the node
 * should fall back to a standard embed card.
 */
export const createConvertNativeEmbedToEmbedCardCommand =
	(href: string): Command =>
	(state, dispatch) => {
		const { embedCard } = state.schema.nodes;
		if (!embedCard) {
			return false;
		}

		const selectedNode = (state.selection as { node?: PMNode }).node;
		if (!selectedNode) {
			return false;
		}

		const embedCardNode = embedCard.createChecked({
			url: href,
			layout: 'center',
		});

		const { from, to } = state.selection;
		const tr = state.tr.replaceWith(from, to, embedCardNode);

		if (dispatch) {
			dispatch(tr.scrollIntoView());
		}

		return true;
	};
